import {
  error as errorIcon,
  info as infoIcon,
  success as successIcon,
  warning as warningIcon,
} from '@/components/icons/icons';
import type {
  ActionForNotification,
  NotificationEntry,
  NotificationMessage,
} from '@/components/notifications/types';
import { t } from 'i18next';
import { DateTime } from 'luxon';
import { defineStore } from 'pinia';
import { v7 as uuidv7 } from 'uuid';
import { readonly, ref } from 'vue';
import { SetRequired } from 'type-fest';

type NotificationDisplayType = 'popUp' | 'bar';

interface NotifyOptions {
  autoHide: boolean;
  store: boolean;
  show: boolean;
  showStackTrace: boolean;
  display: NotificationDisplayType;
}

const defaultNotifyOptions: NotifyOptions = {
  autoHide: true,
  store: false,
  show: true,
  showStackTrace: false,
  display: 'popUp',
};

function adaptiveTimeout(text?: string) {
  if (!text) {
    text = String().padStart(50);
  }
  // increased by 3 seconds because the default is too quick
  return Math.max(Math.min(text.length * 50, 5000), 4500);
}

export const useNotificationStore = defineStore('notification', () => {
  const messages = ref<NotificationEntry[]>([]);

  const currentPopUpMessage = ref<NotificationEntry>();
  const currentBarMessage = ref<NotificationEntry>();

  const timeoutIDs: Record<NotificationDisplayType, ReturnType<typeof setTimeout> | undefined> = {
    popUp: undefined,
    bar: undefined,
  };

  function showPopUpMessage(
    notification: NotificationEntry,
    autoHide = defaultNotifyOptions.autoHide,
    showStackTrace = false,
  ) {
    currentPopUpMessage.value = {
      ...notification,
      stackTrace: showStackTrace ? notification.stackTrace : undefined,
    };

    clearTimeout(timeoutIDs.popUp);

    if (autoHide && notification.type !== 'error')
      timeoutIDs.popUp = setTimeout(hidePopUpMessage, adaptiveTimeout(notification.message));
  }

  function showBarMessage(
    notification: NotificationEntry,
    autoHide = defaultNotifyOptions.autoHide,
  ) {
    currentBarMessage.value = {
      ...notification,
    };

    clearTimeout(timeoutIDs.bar);

    if (autoHide && notification.type !== 'error')
      timeoutIDs.bar = setTimeout(hideBarMessage, adaptiveTimeout(notification.message));
  }

  function hidePopUpMessage() {
    currentPopUpMessage.value = undefined;
  }

  function hideBarMessage() {
    currentBarMessage.value = undefined;
  }

  function addMessage(notification: NotificationMessage | NotificationEntry) {
    // only add the notification if it is not already in the store
    if (useNotificationStore().messages.some((m) => m.message === notification.message)) return;

    messages.value = [...messages.value, { id: uuidv7(), time: DateTime.now(), ...notification }];
  }

  /**
   * Returns the first message entry that matches the search parameters.
   * At least the type and one other parameter must be provided.
   * @param search
   */
  function getMessageEntry(search: SetRequired<Partial<NotificationEntry>, 'type'>) {
    return messages.value.find((message) => {
      const matchesType = message.type === search.type;
      const matchesId = search.id ? message.id === search.id : false;
      const matchesMessage = search.message ? message.message === search.message : false;
      const matchesTitle = search.title ? message.title === search.title : false;
      const matchesTime = search.time ? message.time.toISO() === search.time.toISO() : false;

      return matchesType && (matchesId || matchesMessage || matchesTitle || matchesTime);
    });
  }

  function removeMessage(id: string) {
    messages.value = messages.value.filter((message) => message.id !== id);
  }

  function clearAllMessages() {
    messages.value = [];
  }

  function notify(notification: NotificationMessage, options?: Partial<NotifyOptions>) {
    const optionsWithDefaults: NotifyOptions = {
      ...defaultNotifyOptions,
      ...options,
    };

    const notificationEntry: NotificationEntry = {
      ...notification,
      id: uuidv7(),
      time: DateTime.now(),
    };

    if (optionsWithDefaults.store) {
      addMessage(notificationEntry);
    }

    if (optionsWithDefaults.show) {
      switch (optionsWithDefaults.display) {
        case 'popUp':
          showPopUpMessage(
            notificationEntry,
            optionsWithDefaults.autoHide,
            optionsWithDefaults.showStackTrace,
          );
          break;
        case 'bar':
          showBarMessage(notificationEntry, optionsWithDefaults.autoHide);
          break;
      }
    }
  }

  function success(
    message: string,
    title: string = t('Success'),
    icon = successIcon,
    options?: Partial<NotifyOptions>,
  ) {
    notify(
      {
        title,
        message,
        icon,
        type: 'success',
      },
      {
        display: 'popUp',
        ...options,
      },
    );
  }

  function info(
    message: string,
    title: string = t('Info'),
    icon = infoIcon,
    options?: Partial<NotifyOptions>,
  ) {
    notify(
      {
        title,
        message,
        icon,
        type: 'info',
      },
      {
        display: 'popUp',
        ...options,
      },
    );
  }

  function warning(
    message: string,
    title: string = t('Warning'),
    icon = warningIcon,
    options?: Partial<NotifyOptions>,
  ) {
    notify(
      {
        title,
        message,
        icon,
        type: 'warning',
      },
      options,
    );
  }

  function error(
    message: string,
    notificationAction?: ActionForNotification,
    title: string = t('ErrorTitle'),
    icon = errorIcon,
    options?: Partial<NotifyOptions>,
  ) {
    notify(
      {
        type: 'error',
        title,
        message,
        icon,
        actionForNotification: notificationAction,
      },
      {
        display: 'popUp',
        ...options,
      },
    );
  }

  return {
    notify,
    success,
    info,
    warning,
    error,
    addMessage,
    getMessageEntry,
    removeMessage,
    clearAllMessages,
    hidePopUpMessage,
    hideBarMessage,
    currentPopUpMessage: readonly(currentPopUpMessage),
    currentBarMessage: readonly(currentBarMessage),
    messages: readonly(messages),
  };
});
